<template>
  <div class="admin-tab-content">
    <div class="tab-header">
      <h2>⏰ Programador de Eliminación de Roles</h2>
      <p class="description">Programa la eliminación automática de roles de usuarios en fechas específicas</p>
    </div>

    <!-- Search Section -->
    <div class="search-section">
      <h3>Buscar Usuario</h3>
      <div class="control-section">
        <label for="role-search-type">Buscar por:</label>
        <select id="role-search-type" v-model="searchType" class="admin-select">
          <option value="uid">UID</option>
          <option value="email">Email</option>
        </select>
      </div>

      <div class="control-section">
        <label for="role-search-query">{{ searchType === 'uid' ? 'UID' : 'Email' }}:</label>
        <div class="search-input-wrapper">
          <input
            type="text"
            id="role-search-query"
            v-model="searchQuery"
            @keyup.enter="searchUser"
            class="admin-input"
            :placeholder="`Ingrese ${searchType === 'uid' ? 'UID' : 'email'}`"
          />
          <button @click="searchUser" class="btn-primary" :disabled="searching">
            {{ searching ? 'Buscando...' : 'Buscar' }}
          </button>
        </div>
      </div>
    </div>

    <!-- User Info & Schedule Form -->
    <div v-if="userInfo" class="schedule-section">
      <h3>Usuario Seleccionado</h3>
      <div class="info-card">
        <p><strong>UID:</strong> {{ userInfo.uid }}</p>
        <p><strong>Nombre:</strong> {{ userInfo.name }}</p>
        <p><strong>Email:</strong> {{ userInfo.mail }}</p>
        <div v-if="userInfo.roles && userInfo.roles.length > 0">
          <p><strong>Roles:</strong></p>
          <div class="roles-list">
            <span
              v-for="role in userInfo.roles"
              :key="role"
              class="role-badge"
            >
              {{ role }}
            </span>
          </div>
        </div>
      </div>

      <div class="schedule-form">
        <h4>Programar Eliminación de Rol</h4>

        <div class="control-section">
          <label for="role-select">Rol a eliminar:</label>
          <select id="role-select" v-model="selectedRole" class="admin-select">
            <option value="">Seleccionar rol</option>
            <option
              v-for="role in userInfo.roles"
              :key="role"
              :value="role"
            >
              {{ role }}
            </option>
          </select>
          <p class="help-text">Seleccione el rol que desea programar para eliminación</p>
        </div>

        <div class="control-section">
          <label for="removal-date">Fecha de eliminación:</label>
          <input
            type="date"
            id="removal-date"
            v-model="removalDate"
            class="admin-input"
            :min="minDate"
          />
          <p class="help-text">Fecha en la que se eliminará automáticamente el rol</p>
        </div>

        <div class="action-section">
          <button
            @click="scheduleRemoval"
            class="btn-primary"
            :disabled="!selectedRole || !removalDate || scheduling"
          >
            {{ scheduling ? 'Programando...' : 'Programar Eliminación' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Scheduled Removals Table -->
    <div class="removals-section">
      <div class="section-header">
        <h3>Eliminaciones Programadas</h3>
        <button @click="loadScheduledRemovals" class="btn-secondary" :disabled="loading">
          {{ loading ? 'Actualizando...' : 'Actualizar Lista' }}
        </button>
      </div>

      <div v-if="scheduledRemovals.length > 0" class="table-wrapper">
        <table class="removals-table">
          <thead>
            <tr>
              <th>Usuario</th>
              <th>Email</th>
              <th>Rol</th>
              <th>Fecha Programada</th>
              <th>Programado por</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="removal in scheduledRemovals" :key="removal.key">
              <td>{{ removal.username }}</td>
              <td>{{ removal.email }}</td>
              <td><span class="role-badge">{{ removal.role }}</span></td>
              <td>{{ formatDate(removal.removal_date) }}</td>
              <td>{{ removal.scheduled_by }}</td>
              <td>
                <button
                  @click="cancelRemoval(removal.key)"
                  class="btn-danger"
                  :disabled="canceling"
                >
                  Cancelar
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="no-data">
        No hay eliminaciones programadas
      </div>
    </div>

    <div v-if="message" :class="['message-box', messageType]">
      {{ message }}
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'RoleSchedulerTab',
  data() {
    return {
      searchType: 'uid',
      searchQuery: '',
      searching: false,
      scheduling: false,
      loading: false,
      canceling: false,
      userInfo: null,
      selectedRole: '',
      removalDate: '',
      scheduledRemovals: [],
      message: '',
      messageType: ''
    };
  },
  computed: {
    minDate() {
      const today = new Date();
      return today.toISOString().split('T')[0];
    }
  },
  mounted() {
    this.loadScheduledRemovals();
  },
  methods: {
    async searchUser() {
      if (!this.searchQuery.trim()) {
        this.showMessage('Por favor ingrese un valor de búsqueda', 'error');
        return;
      }

      this.searching = true;
      this.message = '';
      this.userInfo = null;
      this.selectedRole = '';
      this.removalDate = '';

      try {
        const response = await axios.get(`${process.env.VUE_APP_API}/adminmenu/user-features/lookup`, {
          params: {
            query: this.searchQuery,
            type: this.searchType
          }
        });

        if (response.data.success) {
          this.userInfo = response.data.user;

          if (!this.userInfo.roles || this.userInfo.roles.length === 0) {
            this.showMessage('Usuario encontrado pero no tiene roles asignados', 'error');
          }
        } else {
          this.showMessage(response.data.message || 'Usuario no encontrado', 'error');
        }
      } catch (error) {
        this.showMessage('Error al buscar usuario', 'error');
      } finally {
        this.searching = false;
      }
    },
    async scheduleRemoval() {
      if (!this.selectedRole || !this.removalDate) {
        this.showMessage('Por favor complete todos los campos', 'error');
        return;
      }

      this.scheduling = true;
      this.message = '';

      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API}/adminmenu/schedule-role-removal`,
          {
            uid: this.userInfo.uid,
            role: this.selectedRole,
            removal_date: this.removalDate
          }
        );

        if (response.data.success) {
          this.showMessage('Eliminación de rol programada exitosamente', 'success');
          this.selectedRole = '';
          this.removalDate = '';
          this.userInfo = null;
          this.searchQuery = '';
          await this.loadScheduledRemovals();
        } else {
          this.showMessage(response.data.message || 'Error al programar eliminación', 'error');
        }
      } catch (error) {
        this.showMessage('Error al programar eliminación', 'error');
      } finally {
        this.scheduling = false;
      }
    },
    async loadScheduledRemovals() {
      this.loading = true;

      try {
        const response = await axios.get(`${process.env.VUE_APP_API}/adminmenu/get-scheduled-removals`);

        if (response.data.success) {
          this.scheduledRemovals = response.data.scheduled_removals || [];
        }
      } catch (error) {
        console.error('Error loading scheduled removals:', error);
      } finally {
        this.loading = false;
      }
    },
    async cancelRemoval(key) {
      if (!confirm('¿Está seguro que desea cancelar esta programación?')) {
        return;
      }

      this.canceling = true;
      this.message = '';

      try {
        const response = await axios.post(
          `${process.env.VUE_APP_API}/adminmenu/cancel-role-removal`,
          { key }
        );

        if (response.data.success) {
          this.showMessage('Programación cancelada exitosamente', 'success');
          await this.loadScheduledRemovals();
        } else {
          this.showMessage(response.data.message || 'Error al cancelar', 'error');
        }
      } catch (error) {
        this.showMessage('Error al cancelar la programación', 'error');
      } finally {
        this.canceling = false;
      }
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },
    showMessage(msg, type) {
      this.message = msg;
      this.messageType = type;

      if (type === 'success') {
        setTimeout(() => {
          this.message = '';
        }, 3000);
      }
    }
  }
};
</script>

<style scoped>
.admin-tab-content {
  padding: 20px;
}

.tab-header h2 {
  color: #3498db;
  font-size: 24px;
  margin-bottom: 10px;
}

.description {
  color: #bbb;
  margin-bottom: 30px;
}

.search-section,
.schedule-section,
.removals-section {
  background: #2c2c2c;
  padding: 20px;
  border-radius: 6px;
  margin-bottom: 20px;
}

.search-section h3,
.schedule-section h3,
.removals-section h3 {
  color: #3498db;
  margin-bottom: 15px;
  font-size: 18px;
}

.control-section {
  margin-bottom: 15px;
}

.control-section label {
  display: block;
  font-weight: 500;
  margin-bottom: 8px;
  color: #e6e6e6;
}

.search-input-wrapper {
  display: flex;
  gap: 10px;
}

.admin-input {
  background-color: #333;
  border: 1px solid #444;
  border-radius: 4px;
  color: #fff;
  font-size: 14px;
  height: 38px;
  padding: 0 10px;
  flex: 1;
}

.admin-select {
  background-color: #333;
  border: 1px solid #444;
  border-radius: 4px;
  color: #fff;
  font-size: 14px;
  height: 38px;
  padding: 0 10px;
  min-width: 200px;
}

.help-text {
  color: #aaa;
  font-size: 13px;
  margin-top: 5px;
}

.info-card {
  background: #333;
  padding: 15px;
  border-radius: 4px;
  border-left: 3px solid #3498db;
  margin-bottom: 20px;
}

.info-card p {
  margin: 8px 0;
  color: #e6e6e6;
}

.roles-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.role-badge {
  display: inline-block;
  background: #2980b9;
  color: white;
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 12px;
}

.schedule-form {
  background: #333;
  padding: 15px;
  border-radius: 4px;
  border-left: 3px solid #f39c12;
}

.schedule-form h4 {
  color: #f39c12;
  font-size: 16px;
  margin-bottom: 15px;
}

.action-section {
  margin-top: 15px;
}

.btn-primary,
.btn-secondary,
.btn-danger {
  border: none;
  border-radius: 4px;
  color: white;
  cursor: pointer;
  font-size: 14px;
  padding: 10px 20px;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.btn-primary {
  background: #3498db;
}

.btn-primary:hover:not(:disabled) {
  background: #2980b9;
  transform: translateY(-1px);
}

.btn-secondary {
  background: #95a5a6;
}

.btn-secondary:hover:not(:disabled) {
  background: #7f8c8d;
}

.btn-danger {
  background: #e74c3c;
  padding: 6px 12px;
  font-size: 12px;
}

.btn-danger:hover:not(:disabled) {
  background: #c0392b;
}

.btn-primary:disabled,
.btn-secondary:disabled,
.btn-danger:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.table-wrapper {
  overflow-x: auto;
}

.removals-table {
  width: 100%;
  border-collapse: collapse;
  background: #333;
  border-radius: 4px;
  overflow: hidden;
}

.removals-table th {
  background: #444;
  color: #e6e6e6;
  font-weight: 500;
  padding: 12px 10px;
  text-align: left;
  border-bottom: 1px solid #555;
}

.removals-table td {
  padding: 10px;
  border-bottom: 1px solid #444;
  color: #e6e6e6;
}

.removals-table tr:hover {
  background: #3a3a3a;
}

.no-data {
  text-align: center;
  padding: 30px;
  color: #bbb;
  font-style: italic;
  background: #333;
  border-radius: 4px;
}

.message-box {
  background-color: #333;
  border-radius: 4px;
  margin-top: 15px;
  padding: 10px;
  border-left: 3px solid #3498db;
}

.message-box.success {
  border-left-color: #2ecc71;
  color: #2ecc71;
}

.message-box.error {
  border-left-color: #e74c3c;
  color: #e74c3c;
}
</style>
